import { browser } from "$app/environment";
import { page } from "$app/state";
import { type AnalyticsPayload, type LogPayload } from "$lib/api/velatlas/response.types";
import { ANALYTICS_EVENT_NAMES, ENABLE_ANALYTICS_SENDING, ENABLE_LOGGING_TO_SERVER, VELATLAS_API_URL } from "$lib/statics";
import { getUserAgentInfo } from "$lib/utils/analytics.util";
import { generateUUIDv7 } from "$lib/utils/general.util";

interface AnalyticsRequestOptions {
    keepalive?: boolean;
}

interface UserAgentInfo {
    os?: string;
    browser?: string;
    bot?: string;
}

export class ClientVelatlasLoggerApi {
    public static readonly BASE_URL: string = VELATLAS_API_URL;
    private static _sessionId: string | null = null;
    private static _simplifiedUserAgent: UserAgentInfo | null = null;

    // Expose a public lazy getter, so it doesn't run in global state, causing cloudflare to throw an error
    public static get SESSION_ID(): string {
        if (!ClientVelatlasLoggerApi._sessionId) {
            ClientVelatlasLoggerApi._sessionId = generateUUIDv7();
        }
        return ClientVelatlasLoggerApi._sessionId;
    }

    public async sendLog(payload: LogPayload, customFetch?: typeof fetch): Promise<void> {
        if (!ENABLE_LOGGING_TO_SERVER) return;
        // If no customFetch is provided on the client, defer execution to the next tick to use the event fetch.
        if (!customFetch && typeof window !== 'undefined') {
            setTimeout(() => {
                ClientVelatlasLoggerApi.executeSendLog(payload, fetch);
            }, 0);
            return;
        }
        await ClientVelatlasLoggerApi.executeSendLog(payload, customFetch || fetch);
    }

    public async sendAnalytics(
        data: {
            event: ANALYTICS_EVENT_NAMES;
            payload?: Record<string, any> | undefined;
            uri?: string;
            url?: string;
        },
        options?: AnalyticsRequestOptions,
        customFetch?: typeof fetch
    ): Promise<void> {
        if (!ENABLE_ANALYTICS_SENDING) return;

        const routeMetadata = ClientVelatlasLoggerApi.getRouteMetadata();

        const requestPayload: AnalyticsPayload = {
            event: data.event,
            payload: {
                ...getUserAgentInfo(),
                ...data.payload
            },
            uri: data.uri || routeMetadata.uri,
            url: data.url || routeMetadata.url,
            sessionId: ClientVelatlasLoggerApi.SESSION_ID,
            clientCreatedAt: new Date().toISOString(),
        };

        // If keepalive is true, execute immediately to prevent aggressive mobile browser engines (like Firefox on Android) from terminating the JS thread before the fetch is initiated.
        if (options?.keepalive) {
            await ClientVelatlasLoggerApi.executeSendAnalyics(requestPayload, customFetch || fetch, options);
            return;
        }

        // If no customFetch is provided on the client, defer execution to the next tick to use the event fetch.
        if (!customFetch && typeof window !== 'undefined') {
            setTimeout(() => {
                ClientVelatlasLoggerApi.executeSendAnalyics(requestPayload, fetch, options);
            }, 0);
            return;
        }

        await ClientVelatlasLoggerApi.executeSendAnalyics(requestPayload, customFetch || fetch, options);
    }

    private static async executeSendLog(payload: LogPayload, fetchFn: typeof fetch): Promise<void> {
        try {
            const response = await fetchFn(`${ClientVelatlasLoggerApi.BASE_URL}/telemetry/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': this.getCookie('XSRF-TOKEN') ?? '' },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            if (!response.ok) {
                console.warn(`[VelatlasApi] Log submission rejected by API. Status: ${response.status}`);
            }
        } catch (err: any) {
            console.warn('[VelatlasApi] Network failure sending log:', err.message);
        }
    }

    private static async executeSendAnalyics(payload: AnalyticsPayload, fetchFn: typeof fetch, options?: AnalyticsRequestOptions): Promise<void> {
        try {
            const response = await fetchFn(`${ClientVelatlasLoggerApi.BASE_URL}/telemetry/analytics`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': this.getCookie('XSRF-TOKEN') ?? '' },
                body: JSON.stringify(payload),
                credentials: 'include',
                keepalive: options?.keepalive ?? false
            });

            if (!response.ok) {
                console.warn(`[VelatlasApi] Analytics submission rejected by API. Status: ${response.status}`);
            }
        } catch (err: any) {
            console.warn('[VelatlasApi] Network failure sending analytics:', err.message);
        }
    }

    public static getRouteMetadata(): { uri: string; url: string } {
        if (!browser) {
            return { uri: '', url: '' };
        }

        try {
            const currentPath = page.url.pathname || window.location.pathname;
            let routeId = page.route.id;

            // Fallback: Reconstruct using page.params if route.id is null or equal to the raw path
            if ((!routeId || routeId === currentPath) && page.params) {
                let inferredUri = currentPath;

                // Sort params by length, so longer params are matched first, otherwise URL might come up malformed and incorrect
                const sortedParams = Object.entries(page.params)
                    .filter(([_, val]) => val && val.length > 0)
                    .sort((a, b) => b[1].length - a[1].length);

                for (const [key, value] of sortedParams) {
                    inferredUri = inferredUri.replaceAll(`/${value}`, `/[${key}]`);
                }
                routeId = inferredUri;
            }

            // Strips any SvelteKit route matcher (e.g. [param=matcher] -> [param])
            if (routeId) {
                routeId = routeId.replaceAll(/=[\w-]+/g, '');
            }

            return {
                uri: routeId || currentPath,
                url: currentPath
            };
        } catch {
            return {
                uri: window.location.pathname,
                url: window.location.pathname
            };
        }
    }

    private static getCookie(name: string): string | null {
        if (!browser || !document.cookie) {
            return null;
        }
        const foundCookie = document.cookie
            .split('; ')
            .find(row => row.startsWith(name + '='));
        return foundCookie ? foundCookie.split('=')[1] : null;
    }

}